import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useIntl, FormattedMessage } from 'react-intl';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core';
import StatusText, { texts } from 'components/shared/StatusText';
import { DatePicker, Select } from 'components/FormInputs';
import moment from 'logic/moment';

const styles = makeStyles((theme) => ({
  input: {
    [theme.breakpoints.down('xs')]: {
      '& label + .MuiInput-formControl': {
        marginTop: '24px',
      },
    },
  },
}));

const BookingsListForm = ({ initialValues, onSubmit }) => {
  const classes = styles();
  const intl = useIntl();
  const formik = useFormik({
    initialValues: {
      ...initialValues,
    },
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      return onSubmit({
        ...values,
        checkInFrom: moment.isMoment(values.checkInFrom)
          ? values.checkInFrom.toISOString()
          : values.checkInFrom,
        checkInTo: moment.isMoment(values.checkInTo)
          ? values.checkInTo.toISOString()
          : values.checkInTo,
      });
    },
  });

  const { values, errors, handleSubmit, handleBlur, handleChange, setFieldValue, submitForm } =
    formik;

  const onChange = (e) => {
    handleChange(e);

    submitForm();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <DatePicker
            name="checkInFrom"
            value={values.checkInFrom || null}
            onBlur={handleBlur}
            onChange={(date) => {
              if (date || date === null) {
                setFieldValue('checkInFrom', date);

                submitForm();
              }
            }}
            label={<FormattedMessage id="mincheckin" defaultMessage="Min checkin" />}
            errors={errors}
            cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
            clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
            clearable
            format="YYYY-MM-DD"
            className={classes.input}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <DatePicker
            name="checkInTo"
            value={values.checkInTo || null}
            onBlur={handleBlur}
            onChange={(date) => {
              if (date || date === null) {
                setFieldValue('checkInTo', date);

                submitForm();
              }
            }}
            label={<FormattedMessage id="maxcheckin" defaultMessage="Max checkin" />}
            errors={errors}
            cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
            clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
            clearable
            format="YYYY-MM-DD"
            className={classes.input}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Select
            name="order"
            value={values.order}
            onChange={onChange}
            onBlur={handleBlur}
            errors={errors}
            label={<FormattedMessage id="orderby" defaultMessage="Order by" />}
            options={[
              {
                value: 'id ASC',
                label: intl.formatMessage({
                  id: 'bookingOrder.Created',
                  defaultMessage: 'Created',
                }),
              },
              {
                value: 'check_in DESC',
                label: intl.formatMessage({
                  id: 'bookingOrder.Checkin descending',
                  defaultMessage: 'Checkin descending',
                }),
              },
              {
                value: 'check_in ASC',
                label: intl.formatMessage({
                  id: 'bookingOrder.Checkin ascending',
                  defaultMessage: 'Checkin ascending',
                }),
              },
              {
                value: 'paid_at ASC',
                label: intl.formatMessage({
                  id: 'bookingOrder.Payment date',
                  defaultMessage: 'Payment date',
                }),
              },
            ]}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Select
            name="status"
            value={values.status}
            onChange={onChange}
            onBlur={handleBlur}
            errors={errors}
            label={<FormattedMessage id="Filter by" defaultMessage="Filter by" />}
            options={[
              {
                value: '__none__',
                label: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
              },
            ].concat(
              Object.keys(texts).map((name) => ({
                value: name,
                label: <StatusText withoutColor status={name} />,
              })),
            )}
          />
        </Grid>
      </Grid>
    </form>
  );
};

BookingsListForm.propTypes = {
  initialValues: PropTypes.shape({}),
  onSubmit: PropTypes.func.isRequired,
};

export default BookingsListForm;
